import { FC, ReactNode } from 'react'
import {
  CheckOutlined,
  CloseOutlined,
  DollarOutlined,
  EditOutlined,
  InfoCircleOutlined,
  SwapOutlined,
} from '@ant-design/icons'
import { Empty, Spin } from 'antd'
import dayjs from 'dayjs'
import classNames from 'classnames'
import TooltipWrapper from '@UI/New/TooltipWrapper/TooltipWrapper'
import { getLessonReportDateTime } from '@/helpers/lessonReportDateTime'
import { getLessonPaymentState } from '@/helpers/lessonPaymentState'
import cn from './LessonStatusCards.module.css'

export interface ILessonStatusCardItem {
  id: number | string
  dateTime: string
  dateConference?: string | null
  startTime?: string | null
  status?: number | string | null
  statusText?: string | null
  teacher?: string | null
  course?: string | null
  group?: string | null
  duration?: number | string | null
  studentCount?: number | string | null
  visitedCount?: number | string | null
  missedCount?: number | string | null
  income?: number | string | null
  salary?: number | string | null
  profit?: number | string | null
  comment?: string | null
  isPaid?: boolean | number | null
  hasEnoughBalance?: boolean | number | null
  onEdit?: () => void
}

interface ILessonStatusCards {
  items: ILessonStatusCardItem[]
  loading?: boolean
}

const getStatusTitle = (status: number, fallback?: string | null) => {
  if (fallback) return fallback

  switch (status) {
    case 5:
      return 'Проведено'
    case 9:
      return 'Перенесено'
    case 10:
      return 'Отмена учеником'
    case 11:
      return 'Отмена педагогом'
    case 12:
      return 'Платная отмена'
    default:
      return 'Не отчитались'
  }
}

const getStatusIcon = (status: number) => {
  switch (status) {
    case 5:
      return (
        <CheckOutlined
          onPointerEnterCapture={undefined}
          onPointerLeaveCapture={undefined}
        />
      )
    case 9:
      return (
        <SwapOutlined
          onPointerEnterCapture={undefined}
          onPointerLeaveCapture={undefined}
        />
      )
    case 10:
    case 11:
      return (
        <CloseOutlined
          onPointerEnterCapture={undefined}
          onPointerLeaveCapture={undefined}
        />
      )
    case 12:
      return (
        <DollarOutlined
          onPointerEnterCapture={undefined}
          onPointerLeaveCapture={undefined}
        />
      )
    default:
      return null
  }
}

const DetailRow = ({ label, value }: { label: string; value?: ReactNode }) => {
  if (value === null || value === undefined || value === '') return null

  return (
    <div className={cn.detailRow}>
      <span className={cn.detailLabel}>{label}:</span> {value}
    </div>
  )
}

const CardTooltip = ({
  item,
  statusTitle,
  paymentTitle,
}: {
  item: ILessonStatusCardItem
  statusTitle: string
  paymentTitle: string
}) => (
  <div className={cn.details}>
    <DetailRow label="Статус" value={statusTitle} />
    <DetailRow label="Оплата" value={paymentTitle} />
    <DetailRow label="Педагог" value={item.teacher} />
    <DetailRow label="Курс" value={item.course} />
    <DetailRow label="Группа" value={item.group} />
    <DetailRow label="Длительность" value={item.duration} />
    <DetailRow label="Учащихся" value={item.studentCount} />
    <DetailRow label="Проведено для" value={item.visitedCount} />
    <DetailRow label="Не пришло" value={item.missedCount} />
    <DetailRow label="Доход" value={item.income} />
    <DetailRow label="ЗП" value={item.salary} />
    <DetailRow label="Прибыль" value={item.profit} />
    <DetailRow label="Комментарий" value={item.comment} />
  </div>
)

const LessonStatusCards: FC<ILessonStatusCards> = ({ items, loading }) => {
  if (loading) {
    return (
      <div className={cn.state}>
        <Spin />
      </div>
    )
  }

  if (!items.length) {
    return (
      <div className={cn.state}>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Данные не найдены"
        />
      </div>
    )
  }

  return (
    <div className={cn.grid}>
      {items.map((item) => {
        const { date, time } = getLessonReportDateTime({
          date_time: item.dateTime,
          date_conference: item.dateConference,
          start_time: item.startTime,
        })
        const status = Number(item.status ?? 0)
        const payment = getLessonPaymentState(item.isPaid, item.hasEnoughBalance)
        const isToday = date?.isSame(dayjs(), 'day')
        const statusTitle = getStatusTitle(status, item.statusText)

        return (
          <div
            key={item.id}
            className={classNames(
              cn.card,
              status === 5 && cn.done,
              status === 9 && cn.rescheduled,
              (status === 10 || status === 11) && cn.cancelled,
              status === 12 && cn.paidCancellation,
              status === 0 && cn.unreported,
              payment.covered === true && cn.paid,
              payment.covered === false && cn.insufficientBalance,
            )}
            aria-label={`${
              date ? `${date.format('DD.MM.YYYY')} ${time ?? ''}` : 'Дата неизвестна'
            }: ${statusTitle}. ${payment.title}`}
          >
            <div className={cn.cardTop}>
              <TooltipWrapper title={statusTitle}>
                <span className={cn.statusIcon} aria-label={statusTitle} tabIndex={0}>
                  {getStatusIcon(status)}
                </span>
              </TooltipWrapper>
              {payment.covered !== null && (
                <TooltipWrapper title={payment.title}>
                  <span
                    className={classNames(cn.paymentIcon, !payment.covered && cn.crossedPayment)}
                    aria-label={payment.title}
                    tabIndex={0}
                  >
                    <DollarOutlined onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                  </span>
                </TooltipWrapper>
              )}
              <TooltipWrapper
                title={
                  <CardTooltip
                    item={item}
                    statusTitle={statusTitle}
                    paymentTitle={payment.title}
                  />
                }
              >
                <button
                  type="button"
                  className={cn.iconButton}
                  aria-label="Информация о занятии"
                >
                  <InfoCircleOutlined
                    onPointerEnterCapture={undefined}
                    onPointerLeaveCapture={undefined}
                  />
                </button>
              </TooltipWrapper>
            </div>
            <div className={cn.date}>
              {date ? date.format('DD.MM') : '—'}
            </div>
            <div className={cn.time}>
              {time ?? '—'}
            </div>
            {isToday && <span className={cn.today}>Сегодня</span>}
            {item.onEdit && (
              <button
                type="button"
                className={classNames(cn.iconButton, cn.editButton)}
                onClick={item.onEdit}
                aria-label="Редактировать занятие"
              >
                <EditOutlined
                  onPointerEnterCapture={undefined}
                  onPointerLeaveCapture={undefined}
                />
              </button>
            )}
          </div>
        )
      })}
    </div>
  )
}

export default LessonStatusCards
